import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';

import { UntilDestroy } from '@ngneat/until-destroy';

import {
    AlertService,
    ButtonColors,
    ButtonModule,
    CheckboxModule,
    LayerService,
    NumberfieldModule,
    PopupService,
    TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { RouterModule } from '@angular/router';

import {
    BillingMilestoneModel,
    ProjectModel,
    SubscriptionBillingTiming,
} from '../../../../project.models';
import { AddBillingMilestones, UpdateBillingMilestone } from '../../../../project.state';
import { EdDatepickerComponent } from 'src/app/shared/components/forms/ed-datepicker/ed-datepicker.component';
import Swal from 'sweetalert2';
import { convertNumber } from 'src/app/shared/all.helpers';

@UntilDestroy()
@Component({
    selector: 'billing-milestone-add',
    templateUrl: './billing-milestone-add.component.html',
    styleUrls: ['./billing-milestone-add.component.scss'],
    standalone: true,
    schemas: [],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        NumberfieldModule,
        RouterModule,
        EdDatepickerComponent,
        CheckboxModule,
        NumberfieldModule,
    ],
})
export class BillingMilestoneAdd {
    public buttonColors = ButtonColors;

    public header: string = '';
    public errMessage: string = '';

    public project: ProjectModel | undefined;

    public billingList: Partial<BillingMilestoneModel>[] = [];

    public subscriptionBillingTimingOptions = [
        {
            value: SubscriptionBillingTiming.START_OF_PERIOD,
            label: SubscriptionBillingTiming.START_OF_PERIOD,
        },
        {
            value: SubscriptionBillingTiming.END_OF_PERIOD,
            label: SubscriptionBillingTiming.END_OF_PERIOD,
        },
    ];

    constructor(
        private readonly layerService: LayerService,
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly alertService: AlertService,
        private readonly popupService: PopupService,
    ) {
        this.addLine();
    }

    public addBilingMilestone(updateSoldAmount: boolean) {
        if (!this.project) return;
        if (this.billingList.length === 0) return;
        this.store
            .dispatch(
                new AddBillingMilestones({
                    billingMilestones: this.billingList.map((b) => ({ ...b, id: undefined })),
                    projectId: this.project.id,
                    updateSoldAmount,
                }),
            )
            .subscribe({
                next: () => {
                    this.alertService.valid(
                        'Mise à jour',
                        `le planning de facturation a été mis à jour !`,
                    );
                    this.close();
                },
                error: (err: any) => {
                    console.info(err.error.message);
                    this.errMessage = err.error.message;
                    this.alertService.error('Mise à jour', err.error.message);
                },
            });
    }

    isDirty(): boolean {
        return (
            this.billingList.length == 0 ||
            this.billingList.some(
                (b) =>
                    (b.amountExclTax ?? 0) === 0 ||
                    !b.startDate ||
                    (b.endDate && new Date(b.endDate).getTime() < new Date(b.startDate).getTime()),
            )
        );
    }

    async valid(): Promise<void> {
        if (!this.project) return;
        //create
        this.errMessage = '';
        let updateSoldAmount = false;

        if (this.projectSoldAmountMismatch()) {
            await new Promise((resolve, reject) => {
                const diff = this.getSum() - (this.project?.soldAmount || 0);
                let htmlContent = `Souhaitez-vous mettre à jour le montant total de la mission (${convertNumber(diff, 0, true)} €HT) ?`;
                let cancelButtonText = 'Non';

                if (diff > 0) {
                    htmlContent = `La planning de facturation dépasse de ${convertNumber(diff, 0, true)} €HT, le montant total de la mission sera mis à jour.`;
                    cancelButtonText = 'Retour';
                }

                this.popupService
                    .openPopup({
                        title: `Le planning ne correspond plus au montant total de la mission`,
                        htmlContent,
                        cancelButtonText,
                        confirmButtonText: 'Mettre à jour',
                    })
                    .then(async (result) => {
                        if (result.isConfirmed) {
                            updateSoldAmount = true;
                            return resolve(null);
                        }
                        if (result.dismiss !== Swal.DismissReason.close) {
                            if (diff > 0) return reject();
                            else {
                                return resolve(null);
                            }
                        }
                    });
            });
        }
        await this.addBilingMilestone(updateSoldAmount);
    }

    public set params(params: { header: string; project: ProjectModel }) {
        this.header = params.header;
        this.project = params.project;
    }

    public close() {
        this.layerService.close();
    }

    public getSumFormatted() {
        return convertNumber(this.getSum(), 2, false);
    }

    public getSum(): number {
        return (
            (this.project?.billingMilestones?.reduce((tot, b) => tot + b.amountExclTax, 0) ?? 0) +
            (this.billingList.reduce((tot, b) => tot + (b.amountExclTax ?? 0), 0) ?? 0)
        );
    }

    public projectSoldAmountMismatch(): boolean {
        return this.getSum() !== this.project?.soldAmount;
    }

    public hasEndDate() {
        return this.billingList.some((x) => x.endDate);
    }
    public addLine() {
        this.billingList.push({
            id: 'temp_' + new Date().getTime(),
            startDate: new Date(),
            endDate: undefined,
            amountExclTax: undefined,
            isSent: false,
            invoiceNumber: '',
            comment: '',
            subscriptionBillingTiming: SubscriptionBillingTiming.START_OF_PERIOD,
        });
    }
}
